import { useState, useEffect, useRef } from "react";

import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { CONFIG } from "../../config";

import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Save, ArrowLeft, Image as ImageIcon } from "lucide-react";
import toast from "react-hot-toast";

export default function ArticleEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const audioInputRef = useRef(null);
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [uploadingAudio, setUploadingAudio] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [activeLocale, setActiveLocale] = useState("en");

    // Shared Article Data
    const [articleData, setArticleData] = useState({
        slug: "",
        categoryId: "",
        coverImage: "",
        status: "DRAFT",
    });

    // Localized Content Map
    const [translations, setTranslations] = useState({
        en: { title: "", content: "", excerpt: "", keywords: "", videoUrl: "", audioUrl: "" },
        hi: { title: "", content: "", excerpt: "", keywords: "", videoUrl: "", audioUrl: "" }
    });


    const [categories, setCategories] = useState([]);


    useEffect(() => {
        fetchCategories();
        if (isEditing) {
            fetchArticle();
        }
    }, [id]);

    const fetchCategories = async () => {
        try {
            const res = await api.get("/categories");
            setCategories(res.data);
        } catch (e) {
            console.error("Failed to load categories", e);
        }
    };

    const fetchArticle = async () => {
        try {
            const res = await api.get(`/admin/articles/${id}?adminFetch=true`);
            const { slug, categoryId, coverImage, status } = res.data;
            
            setArticleData({
                slug: slug || "",
                categoryId: categoryId || "",
                coverImage: coverImage || "",
                status: status || "DRAFT"
            });

            // Map incoming translations array to our state map
            const newTrans = { ...translations };
            res.data.translations?.forEach(t => {
                if (newTrans[t.locale]) {
                    newTrans[t.locale] = {
                        title: t.title || "",
                        content: t.content || "",
                        excerpt: t.excerpt || "",
                        keywords: t.keywords || "",
                        videoUrl: t.videoUrl || "",
                        audioUrl: t.audioUrl || ""
                    };
                }
            });
            setTranslations(newTrans);

        } catch (err) {
            console.error(err);
            toast.error("The records are blocked.");
            navigate("/admin/articles");
        }
    };

    /**
     * 🪄 AUTO-SAVE SYSTEM (Debounced Drafts)
     */
    useEffect(() => {
        if (!isEditing || loading) return;

        const timer = setTimeout(() => {
            performAutoSave();
        }, 15000); // 15s debounce for peace of mind

        return () => clearTimeout(timer);
    }, [translations, articleData]);

    const performAutoSave = async () => {
        try {
            const payload = {
                ...articleData,
                ...translations[activeLocale],
                locale: activeLocale
            };
            await api.put(`/admin/articles/${id}`, payload);
            setLastSaved(new Date());
        } catch (err) {
            console.warn("Silent save failed (connection ripple)");
        }
    };

    const handleSharedChange = (e) => {
        const { name, value } = e.target;
        setArticleData(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationChange = (e) => {
        const { name, value } = e.target;
        setTranslations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], [name]: value }
        }));
    };

    const handleContentChange = (content) => {
        setTranslations(prev => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], content }
        }));
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Size check: 20MB
        if (file.size > 20 * 1024 * 1024) {
            return toast.error("Recording is too heavy (Max 20MB).");
        }

        const formDataAudio = new FormData();
        formDataAudio.append("audio", file);

        setUploadingAudio(true);
        const toastId = toast.loading("Uploading sacred recording...");

        try {
            const res = await api.post("/admin/upload-audio", formDataAudio, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            
            setTranslations(prev => ({
                ...prev,
                [activeLocale]: { ...prev[activeLocale], audioUrl: res.data.url }
            }));
            toast.success("Recording uploaded!", { id: toastId });
        } catch (err) {
            console.error("Upload failed", err);
            toast.error("Upload failed. Is the format MP3 or WAV?", { id: toastId });
        } finally {
            setUploadingAudio(false);
        }
    };


    const handleSubmit = async (e, forcedStatus) => {
        if (e) e.preventDefault();
        
        const current = translations[activeLocale];
        if (!current.title || !current.content) {
            return toast.error("A scroll needs a title and wisdom before it can be shared.");
        }

        setLoading(true);
        const targetStatus = forcedStatus || articleData.status;

        try {
            const payload = {
                ...articleData,
                ...current,
                locale: activeLocale,
                status: targetStatus
            };

            if (isEditing) {
                await api.put(`/admin/articles/${id}`, payload);
                toast.success(targetStatus === 'PUBLISHED' ? "Scroll Published." : "Draft Saved.");
            } else {
                await api.post("/admin/articles", payload);
                toast.success("New wisdom inked.");
            }
            if (targetStatus === 'PUBLISHED') navigate("/admin/articles");
        } catch (err) {
            console.error(err);
            const reason = err?.response?.data?.error || "The link between servers was broken.";
            toast.error(reason, { duration: 7000 });
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    return (
        <div className="max-w-5xl mx-auto py-10 px-4 min-h-screen space-y-10">
            {/* Sticky Controls */}
            <div className="flex items-center justify-between bg-black/40 backdrop-blur-md p-4 rounded-2xl border border-white/10 sticky top-0 z-40">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate("/admin/articles")}
                        className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="h-4 w-[1px] bg-white/10" />
                    <h2 className="text-lg font-light text-white">
                        {isEditing ? "Refine Wisdom" : "New Inking"}
                    </h2>
                </div>

                <div className="flex items-center gap-4">
                    {lastSaved && (
                        <span className="text-[10px] text-gray-500 italic">
                            Auto-saved at {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={(e) => handleSubmit(e, 'DRAFT')}
                        disabled={loading}
                        className="text-sm text-gray-300 hover:text-white px-4 py-2"
                    >
                        Save as Draft
                    </button>
                    <button
                        type="button"
                        onClick={(e) => handleSubmit(e, 'PUBLISHED')}
                        disabled={loading}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
                    >
                        <Save size={16} /> Publish
                    </button>
                </div>
            </div>

            {/* Language Switcher Tabs */}
            <div className="flex gap-2">
                {["en", "hi"].map(lang => (
                    <button
                        key={lang}
                        type="button"
                        onClick={() => setActiveLocale(lang)}
                        className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${
                            activeLocale === lang 
                            ? "bg-white text-black" 
                            : "bg-white/5 text-gray-500 hover:bg-white/10"
                        }`}
                    >
                        {lang === "en" ? "English" : "Hinglish"}
                        {translations[lang].title && <span className="ml-2 w-1 h-1 rounded-full bg-green-500 inline-block" />}
                    </button>
                ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Meta Card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
                    {/* Title */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Title ({activeLocale.toUpperCase()})</label>
                            <input
                                type="text"
                                name="title"
                                value={translations[activeLocale].title}
                                onChange={handleTranslationChange}
                                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-lg font-bold text-white focus:outline-none focus:border-cyan-500/50"
                                placeholder="The Art of Stillness..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">URL Slug (Shared)</label>
                            <input
                                type="text"
                                name="slug"
                                value={articleData.slug}
                                onChange={handleSharedChange}
                                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-lg text-gray-300 focus:outline-none focus:border-cyan-500/50"
                                placeholder="art-of-stillness"
                            />
                            <p className="text-[10px] text-gray-500 mt-1">Global URL for all translations.</p>
                        </div>
                    </div>

                    {/* Excerpt */}
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Summary ({activeLocale.toUpperCase()})</label>
                        <textarea
                            name="excerpt"
                            value={translations[activeLocale].excerpt}
                            onChange={handleTranslationChange}
                            rows={2}
                            className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 resize-none"
                            placeholder="A brief soul-stirring summary..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Category */}
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Category</label>
                            <select
                                name="categoryId"
                                value={articleData.categoryId}
                                onChange={handleSharedChange}
                                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-white focus:outline-none"
                            >
                                <option value="">Select Category</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Published Status</label>
                            <select
                                name="status"
                                value={articleData.status}
                                onChange={handleSharedChange}
                                className={`w-full bg-black/40 border border-white/10 rounded-lg p-3 font-bold focus:outline-none ${
                                    articleData.status === 'PUBLISHED' ? 'text-green-400' : 'text-amber-400'
                                }`}
                            >
                                <option value="DRAFT">Draft (Seer's Eyes Only)</option>
                                <option value="PUBLISHED">Published (Seeker's Light)</option>
                            </select>
                        </div>
                    </div>

                    {/* Media Assets */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Video URL */}
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">YouTube Video URL ({activeLocale.toUpperCase()})</label>
                            <div className="relative">
                                <span className="absolute left-3 top-3 text-gray-500 text-xs font-bold">YT</span>
                                <input
                                    type="text"
                                    name="videoUrl"
                                    value={translations[activeLocale].videoUrl}
                                    onChange={handleTranslationChange}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 pl-10 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                                    placeholder="https://youtube.com/watch?v=..."
                                />
                            </div>
                        </div>

                        {/* Audio URL */}
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm text-gray-400">Audio URL ({activeLocale.toUpperCase()})</label>
                                {translations[activeLocale].audioUrl && (
                                    <a 
                                        href={translations[activeLocale].audioUrl.startsWith("/") ? `${CONFIG.API_HOST}${translations[activeLocale].audioUrl}` : translations[activeLocale].audioUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[10px] text-cyan-400 hover:underline flex items-center gap-1"
                                    >
                                        Listen ↓
                                    </a>
                                )}
                            </div>
                            <div className="relative">
                                <span className="absolute left-3 top-3 text-gray-500 text-xs font-bold">MP3</span>
                                <input
                                    type="text"
                                    name="audioUrl"
                                    value={translations[activeLocale].audioUrl}
                                    onChange={handleTranslationChange}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 pl-10 pr-32 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                                    placeholder="https://.../audio.mp3"
                                />
                                <button
                                    type="button"
                                    disabled={uploadingAudio}
                                    onClick={() => audioInputRef.current?.click()}
                                    className="absolute right-2 top-2 bottom-2 px-3 bg-white/10 hover:bg-white/20 text-xs text-white rounded-md transition-colors disabled:opacity-50"
                                >
                                    {uploadingAudio ? "..." : "Upload"}
                                </button>
                                <input 
                                    type="file"
                                    ref={audioInputRef}
                                    onChange={handleAudioUpload}
                                    accept="audio/*"
                                    className="hidden"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Cover Image URL */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Shared Cover Image URL</label>
                            <div className="relative">
                                <ImageIcon className="absolute left-3 top-3 text-gray-500" size={18} />
                                <input
                                    type="text"
                                    name="coverImage"
                                    value={articleData.coverImage}
                                    onChange={handleSharedChange}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 pl-10 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        {/* Keywords */}
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">SEO Keywords ({activeLocale.toUpperCase()})</label>
                            <input
                                type="text"
                                name="keywords"
                                value={translations[activeLocale].keywords}
                                onChange={handleTranslationChange}
                                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                                placeholder="healing, meditation, calm"
                            />
                        </div>
                    </div>
                </div>

                {/* Editor */}
                <div className="bg-white text-black rounded-xl overflow-hidden min-h-[400px]">
                    <ReactQuill
                        theme="snow"
                        value={translations[activeLocale].content}
                        onChange={handleContentChange}
                        modules={modules}
                        className="h-full min-h-[350px]"
                    />
                </div>

                {/* Final Status */}
                <div className="flex justify-between items-center bg-white/5 p-6 rounded-xl border border-white/10">
                    <div>
                        <p className="text-sm font-bold text-white mb-1">Submission Status</p>
                        <p className="text-[10px] text-gray-400">Current version: <span className="text-cyan-400 font-bold uppercase">{activeLocale}</span></p>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-brand-gradient text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                        {loading ? "Inking..." : "Save Wisdom"}
                    </button>
                </div>
            </form>
        </div>
    );
}
